import { useState } from "react";
import { FileText, Loader2, ImageOff, Receipt, ExternalLink, Share2, Play, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useBlobUrl, openBlob } from "./useBlobUrl";
import { ImageViewer } from "./ImageViewer";
import { fmtSize } from "@/lib/format";

const ImageMedia = ({ client, path, id }) => {
  const { url, error } = useBlobUrl(client, path);
  const [open, setOpen] = useState(false);
  if (error) {
    return <div className="flex h-40 w-64 max-w-full items-center justify-center gap-2 rounded-md bg-black/5 text-sm"><ImageOff className="h-5 w-5" /> Imagem indisponível</div>;
  }
  if (!url) {
    return <div className="flex h-48 w-64 max-w-full items-center justify-center rounded-md bg-black/5"><Loader2 className="h-6 w-6 animate-spin text-zap-green" /></div>;
  }
  return (
    <>
    <button type="button" data-testid={`media-image-${id}`} onClick={() => setOpen(true)} className="block overflow-hidden rounded-md">
      <img src={url} alt="Fotografia enviada" className="max-h-80 w-auto max-w-full object-cover"
        onLoad={(e) => { const m = e.currentTarget.closest("main"); if (m && m.scrollHeight - m.scrollTop - m.clientHeight < 700) m.scrollTo({ top: m.scrollHeight }); }} />
    </button>
    {open && <ImageViewer url={url} onClose={() => setOpen(false)} />}
    </>
  );
};

const share = async (url, name, type) => {
  try {
    const blob = await (await fetch(url)).blob();
    const file = new File([blob], name, { type: type || blob.type });
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: "ZAP IUDP" });
      return;
    }
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    toast.info("Este aparelho não permite compartilhar direto. O arquivo foi baixado para você enviar.");
  } catch (e) {
    if (e?.name !== "AbortError") toast.error("Não foi possível compartilhar.");
  }
};

const AudioMedia = ({ client, path, id, file }) => {
  const { url, error } = useBlobUrl(client, path);
  if (error) return <p className="text-sm">Áudio indisponível</p>;
  if (!url) return <div className="flex h-12 w-64 items-center"><Loader2 className="h-5 w-5 animate-spin text-zap-green" /></div>;
  return (
    <div>
      <audio data-testid={`media-audio-${id}`} controls src={url} className="h-12 w-[260px] max-w-full" preload="metadata" />
      <button type="button" data-testid={`media-audio-share-${id}`} onClick={() => share(url, (file.name || "audio").replace(/\.[^.]+$/, "") + ".mp3", file.content_type)}
        className="mt-1 flex h-10 items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-zap-deep active:bg-black/5">
        <Share2 className="h-4 w-4" />Compartilhar
      </button>
    </div>
  );
};

const FileMedia = ({ client, path, file, id, receipt }) => {
  const open = async () => {
    try {
      await openBlob(client, path);
    } catch {
      toast.error("Não foi possível abrir o arquivo.");
    }
  };
  const Icon = receipt ? Receipt : FileText;
  return (
    <button type="button" data-testid={`media-file-${id}`} onClick={open}
      className="flex w-64 max-w-full items-center gap-3 rounded-md bg-black/5 px-3 py-3 text-left transition-colors hover:bg-black/10">
      <Icon className="h-8 w-8 shrink-0 text-zap-deep" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-semibold">{file.name}</span>
        <span className="block text-sm opacity-70">{fmtSize(file.size)} · Abrir</span>
      </span>
      <ExternalLink className="h-4 w-4 shrink-0 opacity-60" />
    </button>
  );
};

const VideoMedia = ({ client, path, id, file }) => {
  const { url, error } = useBlobUrl(client, path);
  if (error) return <p className="text-sm">Vídeo indisponível</p>;
  if (!url) return <div className="flex h-40 w-64 max-w-full items-center justify-center rounded-md bg-black/80"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>;
  return (
    <div>
      <video data-testid={`media-video-${id}`} controls playsInline preload="metadata" src={url} className="max-h-80 w-[280px] max-w-full rounded-md bg-black" />
      <button type="button" data-testid={`media-video-share-${id}`} onClick={() => share(url, file.name || "video.mp4", "video/mp4")}
        className="mt-1 flex h-10 items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-zap-deep active:bg-black/5">
        <Share2 className="h-4 w-4" />Compartilhar
      </button>
    </div>
  );
};

const YouTubeMedia = ({ vid, id }) => {
  const [play, setPlay] = useState(false);
  if (play) {
    return <iframe data-testid={`media-youtube-player-${id}`} title="Vídeo do YouTube" src={`https://www.youtube-nocookie.com/embed/${vid}?autoplay=1&playsinline=1&rel=0`}
      allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen className="aspect-video w-[280px] max-w-full rounded-md bg-black" />;
  }
  return (
    <div>
      <button type="button" data-testid={`media-youtube-${id}`} onClick={() => setPlay(true)} aria-label="Assistir vídeo" className="relative block w-[280px] max-w-full overflow-hidden rounded-md">
        <img src={`https://i.ytimg.com/vi/${vid}/hqdefault.jpg`} alt="Capa do vídeo" className="aspect-video w-full object-cover" />
        <span className="absolute inset-0 flex items-center justify-center"><span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/70"><Play className="ml-1 h-7 w-7 fill-white text-white" /></span></span>
      </button>
      <a href={`https://www.youtube.com/watch?v=${vid}`} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex h-10 items-center gap-1.5 px-3 text-sm font-semibold text-zap-deep">
        <ExternalLink className="h-4 w-4" />Abrir no YouTube
      </a>
    </div>
  );
};

const LinkMedia = ({ url, id }) => {
  let host = url;
  try { host = new URL(url).hostname.replace(/^www\./, ""); } catch { /* keep raw */ }
  return (
    <a data-testid={`media-link-${id}`} href={url} target="_blank" rel="noopener noreferrer"
      className="flex w-[280px] max-w-full items-center gap-3 rounded-md border border-zap-line bg-white/70 p-3 transition-colors hover:bg-white">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-zap-deep text-white"><Link2 className="h-5 w-5" /></span>
      <span className="min-w-0">
        <span className="block text-[15px] font-bold text-zap-deep">Toque para abrir o link</span>
        <span className="block truncate text-xs text-muted-foreground">{host}</span>
      </span>
    </a>
  );
};

export const MessageMedia = ({ msg, client, base }) => {
  if (msg.kind === "youtube" && msg.youtube_id) return <div className="mb-1"><YouTubeMedia vid={msg.youtube_id} id={msg.id} /></div>;
  if (msg.kind === "link" && msg.link_url) return <div className="mb-1"><LinkMedia url={msg.link_url} id={msg.id} /></div>;
  const f = msg.file;
  if (!f) return null;
  const path = `${base}/${f.id}`;
  const isImage = f.content_type.startsWith("image/");
  return (
    <div className="mb-1">
      {msg.kind === "receipt" && (
        <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-zap-gold"><Receipt className="h-4 w-4" /> Comprovante Pix</p>
      )}
      {msg.kind === "video" ? (
        <VideoMedia client={client} path={path} id={msg.id} file={f} />
      ) : msg.kind === "audio" ? (
        <AudioMedia client={client} path={path} id={msg.id} file={f} />
      ) : isImage ? (
        <ImageMedia client={client} path={path} id={msg.id} />
      ) : (
        <FileMedia client={client} path={path} file={f} id={msg.id} receipt={msg.kind === "receipt"} />
      )}
    </div>
  );
};
